import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
	Alert,
	Button,
	Form,
	Input,
	List,
	Modal,
	Radio,
	Space,
	Spin,
	Tag,
	Typography,
} from "antd";
import {
	CheckCircleOutlined,
	CloseCircleOutlined,
	ExclamationCircleOutlined,
	MergeCellsOutlined,
} from "@ant-design/icons";
import { Patient } from "../../../types/healthcare/PatientTypes";
import { MergeDirection } from "./PatientsPage";
import {
	getMergeStatus,
	MergeAmendedSearchCriteria,
	MergeAmendedSearchRequest,
	MergeIhiReviewOutcome,
	MergeJobStatus,
	MergeStatusResponse,
} from "../../../api/healthcare/PatientApi";
import { MERGE_AMENDED_SEARCH_FIELDS, MergeAttemptResult, MergeOutcome } from "./mergeOutcome";
import { validateMedicareNumber } from "../../../utils/healthcare/ValidateMedicareNumber";
import { normaliseMedicareNumberForApi } from "../../../utils/healthcare/NormaliseMedicareNumber";
import { validateFullDateOfBirth } from "../../../utils/healthcare/ValidateDateOfBirth";
import { canReviewHiAlerts } from "../../../utils/userPrefs/validatePermissions";
import { reviewReasonLabel, reviewSeverityPresentation } from "../../admin/hi-alerts/hiAlertLabels";
import { REVIEW_SEVERITY_ALERT } from "../../../types/admin/hiAlerts";
import StatusTag from "../../../components/shared/StatusTag";

const { Text, Title } = Typography;

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 30;

const TERMINAL_STATUSES: MergeJobStatus[] = ["completed", "partial_failure", "failed"];

type Phase = "select" | "polling" | "done" | "timeout" | "outcome" | "amend";

const DOB_DDMMYYYY = /^(\d{2})\/(\d{2})\/(\d{4})$/;

const toIsoDate = (value: string): string => {
	const match = DOB_DDMMYYYY.exec(value);
	return match ? `${match[3]}-${match[2]}-${match[1]}` : value;
};

const validateMedicare = (_rule: unknown, value: unknown) => {
	if (typeof value !== "string" || value.trim() === "") {
		return Promise.resolve();
	}
	const result = validateMedicareNumber(value);
	return result.isValid ? Promise.resolve() : Promise.reject(new Error(result.error));
};

const validateDob = (_rule: unknown, value: unknown) => {
	if (typeof value !== "string" || !DOB_DDMMYYYY.test(value.trim())) {
		return Promise.resolve();
	}
	const result = validateFullDateOfBirth(toIsoDate(value.trim()));
	return result.isValid ? Promise.resolve() : Promise.reject(new Error(result.error));
};

// CU-14ygtkj9tu0: built from the five Search Type 3 fields only, never by spreading the form object.
const buildAmendedCriteria = (values: Record<string, unknown>): MergeAmendedSearchCriteria => {
	const text = (key: keyof MergeAmendedSearchCriteria): string => String(values[key] ?? "").trim();
	return {
		medicare_card_number: normaliseMedicareNumberForApi(text("medicare_card_number")),
		family_name: text("family_name"),
		given_name: text("given_name"),
		date_of_birth: toIsoDate(text("date_of_birth")),
		sex: text("sex"),
	};
};

const renderAmendField = (field: keyof MergeAmendedSearchCriteria): React.ReactNode => {
	switch (field) {
		case "medicare_card_number":
			return (
				<Form.Item
					key={field}
					label="Medicare Card Number"
					name={field}
					rules={[
						{ required: true, whitespace: true, message: "Medicare card number is required" },
						{ validator: validateMedicare },
					]}
				>
					<Input autoComplete="off" placeholder="Medicare Card Number" />
				</Form.Item>
			);
		case "given_name":
			return (
				<Form.Item
					key={field}
					label="First Name"
					name={field}
					rules={[
						{ required: true, whitespace: true, message: "First name is required" },
						{ max: 100, message: "First name must be 100 characters or fewer" },
					]}
				>
					<Input autoComplete="off" placeholder="First Name" />
				</Form.Item>
			);
		case "family_name":
			return (
				<Form.Item
					key={field}
					label="Last Name"
					name={field}
					rules={[
						{ required: true, whitespace: true, message: "Last name is required" },
						{ max: 100, message: "Last name must be 100 characters or fewer" },
					]}
				>
					<Input autoComplete="off" placeholder="Last Name" />
				</Form.Item>
			);
		case "date_of_birth":
			return (
				<Form.Item
					key={field}
					label="Date of Birth"
					name={field}
					rules={[
						{ required: true, whitespace: true, message: "Date of birth is required" },
						{ pattern: DOB_DDMMYYYY, message: "Date of birth must be in DD/MM/YYYY format" },
						{ validator: validateDob },
					]}
				>
					<Input autoComplete="off" placeholder="DD/MM/YYYY" />
				</Form.Item>
			);
		case "sex":
			return (
				<Form.Item
					key={field}
					label="Sex"
					name={field}
					rules={[
						{ required: true, whitespace: true, message: "Sex is required" },
						{ max: 10, message: "Sex must be 10 characters or fewer" },
					]}
				>
					<Input autoComplete="off" placeholder="Sex" />
				</Form.Item>
			);
	}
};

interface MergePatientProps {
	selectedPatients: Patient[];
	modalOpen: boolean;
	/** Triggers the merge API call, optionally as an explicit amended search, and reports what happened. */
	confirmMerge: (amendedSearch?: MergeAmendedSearchRequest) => Promise<MergeAttemptResult>;
	cancelMerge: () => void;
	mergeDirection: MergeDirection;
	setMergeDirection: React.Dispatch<React.SetStateAction<MergeDirection>>;
}

const MergePatientModal: React.FC<MergePatientProps> = ({
	modalOpen,
	confirmMerge,
	cancelMerge,
	mergeDirection,
	setMergeDirection,
	selectedPatients,
}) => {
	const [phase, setPhase] = useState<Phase>("select");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [mergeResult, setMergeResult] = useState<MergeStatusResponse | null>(null);
	const [pollError, setPollError] = useState<string | null>(null);
	const [outcome, setOutcome] = useState<MergeOutcome | null>(null);
	const [amendError, setAmendError] = useState<MergeOutcome | null>(null);
	const [ihiReviews, setIhiReviews] = useState<MergeIhiReviewOutcome[]>([]);
	const [amendForm] = Form.useForm();

	const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const pollCountRef = useRef(0);
	// CU-14ygtkj9tu0: guards against a second request before the first settles; each offer is submitted at most once.
	const submittingRef = useRef(false);
	const usedOffersRef = useRef(new Set<number>());

	// Reset internal state when modal opens
	useEffect(() => {
		if (modalOpen) {
			setPhase("select");
			setIsSubmitting(false);
			setMergeResult(null);
			setPollError(null);
			setOutcome(null);
			setAmendError(null);
			setIhiReviews([]);
			pollCountRef.current = 0;
		} else {
			stopPolling();
		}
	}, [modalOpen]);

	// Stop polling on unmount
	useEffect(() => {
		return () => stopPolling();
	}, []);

	const stopPolling = () => {
		if (pollRef.current) {
			clearTimeout(pollRef.current);
			pollRef.current = null;
		}
	};

	const startPolling = useCallback((mergeId: number) => {
		const poll = async () => {
			if (pollCountRef.current >= MAX_POLLS) {
				stopPolling();
				setPhase("timeout");
				return;
			}
			pollCountRef.current += 1;

			try {
				const status = await getMergeStatus(mergeId);
				setMergeResult(status);

				if (TERMINAL_STATUSES.includes(status.status)) {
					stopPolling();
					setPhase("done");
				} else {
					pollRef.current = setTimeout(poll, POLL_INTERVAL_MS);
				}
			} catch {
				stopPolling();
				setPollError(
					"Lost connection while tracking merge progress. The merge may still be running — refresh the patient list to check."
				);
				setPhase("done");
			}
		};
		poll();
	}, []);

	const beginSubmit = (): boolean => {
		if (submittingRef.current) return false;
		submittingRef.current = true;
		setIsSubmitting(true);
		return true;
	};

	const endSubmit = () => {
		submittingRef.current = false;
		setIsSubmitting(false);
	};

	const showAttemptResult = (result: MergeAttemptResult) => {
		setIhiReviews(result.kind === "started" ? result.ihiReviews ?? [] : []);
		if (result.kind === "started") {
			setOutcome(null);
			pollCountRef.current = 0;
			setPhase("polling");
			startPolling(result.mergeId);
		} else if (result.kind === "outcome") {
			setOutcome(result.outcome);
			setPhase("outcome");
		} else {
			// The parent already showed an error — stay on select phase
			setOutcome(null);
			setPhase("select");
		}
	};

	const handleConfirm = async () => {
		if (!beginSubmit()) return;
		try {
			showAttemptResult(await confirmMerge());
		} finally {
			endSubmit();
		}
	};

	const leaveAmendment = () => {
		amendForm.resetFields();
		setAmendError(null);
	};

	const handleAmendCancel = () => {
		leaveAmendment();
		setPhase("outcome");
	};

	const handleAmendSubmit = async () => {
		const offer = outcome?.amendedSearch;
		if (!offer || usedOffersRef.current.has(offer.amendsMergeId) || !beginSubmit()) return;
		try {
			let values: Record<string, unknown>;
			try {
				values = await amendForm.validateFields();
			} catch {
				return;
			}
			usedOffersRef.current.add(offer.amendsMergeId);
			const result = await confirmMerge({ amends_merge_id: offer.amendsMergeId, criteria: buildAmendedCriteria(values) });
			if (result.kind === "outcome" && result.outcome.kind === "amendment_invalid") {
				// CU-14ygtkj9tu0: refused before the attempt was consumed, so the operator may correct and resubmit.
				usedOffersRef.current.delete(offer.amendsMergeId);
				setAmendError(result.outcome);
				return;
			}
			leaveAmendment();
			showAttemptResult(result);
		} finally {
			endSubmit();
		}
	};

	const getPatientDisplayName = (patient: Patient) =>
		`${patient.first_name || ""} ${patient.last_name || ""}`.trim() ||
		patient.email ||
		patient.id ||
		"Unknown Patient";

	if (!selectedPatients || selectedPatients.length !== 2) {
		return null;
	}

	const patient0 = selectedPatients[0];
	const patient1 = selectedPatients[1];

	// ── Render helpers ────────────────────────────────────────────────────────

	const renderSelect = () => (
		<div style={{ marginTop: 16 }}>
			<Title level={5} style={{ marginBottom: 8 }}>
				Select Merge Direction
			</Title>
			<Text type="secondary" style={{ display: "block", marginBottom: 24 }}>
				Choose which patient profile to keep as the primary record. The other profile's
				data will be merged into the selected primary profile.
			</Text>

			<Radio.Group
				value={mergeDirection}
				onChange={(e) => setMergeDirection(e.target.value as MergeDirection)}
				style={{ width: "100%" }}
			>
				<Space direction="vertical" style={{ width: "100%" }} size="middle">
					<div
						style={{
							padding: 16,
							border: "1px solid #d9d9d9",
							borderRadius: 6,
							background: mergeDirection === "up" ? "#f0f5ff" : "transparent",
						}}
					>
						<Radio value="up" style={{ marginBottom: 8 }}>
							<Text strong>Keep {getPatientDisplayName(patient1)} as Primary</Text>
						</Radio>
						<div style={{ marginLeft: 24, fontSize: 13, color: "#595959" }}>
							<div style={{ marginBottom: 8 }}>
								<Text strong>Primary:</Text> {getPatientDisplayName(patient1)}
								<br />
								<Text strong>Email:</Text> {patient1.email}
								<br />
								<Text strong>Phone:</Text> {patient1.phone || "N/A"}
							</div>
							<div style={{ marginBottom: 8 }}>
								<Text strong>Merge from:</Text> {getPatientDisplayName(patient0)}
								<br />
								<Text strong>Email:</Text> {patient0.email}
								<br />
								<Text strong>Phone:</Text> {patient0.phone || "N/A"}
							</div>
							<div style={{ fontSize: 11, color: "#8c8c8c" }}>
								Direction: [{patient0.id?.toString().slice(-4) || "0"}] → [
								{patient1.id?.toString().slice(-4) || "1"}]
							</div>
						</div>
					</div>

					<div
						style={{
							padding: 16,
							border: "1px solid #d9d9d9",
							borderRadius: 6,
							background: mergeDirection === "down" ? "#f0f5ff" : "transparent",
						}}
					>
						<Radio value="down" style={{ marginBottom: 8 }}>
							<Text strong>Keep {getPatientDisplayName(patient0)} as Primary</Text>
						</Radio>
						<div style={{ marginLeft: 24, fontSize: 13, color: "#595959" }}>
							<div style={{ marginBottom: 8 }}>
								<Text strong>Primary:</Text> {getPatientDisplayName(patient0)}
								<br />
								<Text strong>Email:</Text> {patient0.email}
								<br />
								<Text strong>Phone:</Text> {patient0.phone || "N/A"}
							</div>
							<div style={{ marginBottom: 8 }}>
								<Text strong>Merge from:</Text> {getPatientDisplayName(patient1)}
								<br />
								<Text strong>Email:</Text> {patient1.email}
								<br />
								<Text strong>Phone:</Text> {patient1.phone || "N/A"}
							</div>
							<div style={{ fontSize: 11, color: "#8c8c8c" }}>
								Direction: [{patient1.id?.toString().slice(-4) || "1"}] → [
								{patient0.id?.toString().slice(-4) || "0"}]
							</div>
						</div>
					</div>
				</Space>
			</Radio.Group>

			<Alert
				message={
					<span>
						<strong>Warning:</strong> This action cannot be undone. The merged profile
						will be permanently deleted.
					</span>
				}
				type="warning"
				icon={<ExclamationCircleOutlined />}
				showIcon
				style={{ marginTop: 24 }}
			/>
		</div>
	);

	const renderPolling = () => (
		<Space
			direction="vertical"
			size="large"
			style={{ width: "100%", textAlign: "center", padding: "32px 0" }}
		>
			<Spin size="large" />
			<div>
				<Title level={5} style={{ marginBottom: 4 }}>
					Merge in progress…
				</Title>
				<Text type="secondary">
					Records are being moved across services. This usually takes a few seconds.
				</Text>
			</div>
		</Space>
	);

	const hiReviewsLink = () =>
		canReviewHiAlerts() && (
			<Link to="/admin/hi-alerts" data-testid="patients-merge-hi-reviews-link">
				Open HI Reviews
			</Link>
		);

	// CU-14ygtkjc8yy: the reviews this attempt raised, in the HI Reviews labels; only a reviewer is told they can act on them.
	const renderIhiReviewList = (reviews: MergeIhiReviewOutcome[]) => (
		<div data-testid="patients-merge-ihi-reviews">
			<List
				size="small"
				split={false}
				dataSource={reviews}
				renderItem={(review) => {
					const severity = reviewSeverityPresentation(review.severity);
					return (
						<List.Item key={review.reason} style={{ padding: "4px 0" }}>
							<Space wrap>
								<StatusTag status={severity.severity} label={severity.label} color={severity.color} />
								<Text>{reviewReasonLabel(review.reason)}</Text>
								<Text type="secondary">{review.requirement_id}</Text>
							</Space>
						</List.Item>
					);
				}}
			/>
			<div>
				{canReviewHiAlerts()
					? "It is open in HI Reviews for the surviving record until it is reviewed and resolved."
					: "An authorised HI Reviewer must review and resolve it. Your account does not have access to HI Reviews."}
			</div>
		</div>
	);

	const renderRaisedIhiReviews = (style: React.CSSProperties = { marginBottom: 16 }) =>
		ihiReviews.length > 0 && (
			<Alert
				data-testid="patients-merge-ihi-review-notice"
				type="warning"
				showIcon
				message={
					ihiReviews.some((review) => reviewSeverityPresentation(review.severity).severity === REVIEW_SEVERITY_ALERT)
						? "HI alert raised for the surviving record"
						: "HI warning raised for the surviving record"
				}
				description={
					<>
						{renderIhiReviewList(ihiReviews)}
						{hiReviewsLink()}
					</>
				}
				style={style}
			/>
		);

	const renderResults = () => {
		if (pollError) {
			return (
				<>
					<Alert
						type="warning"
						showIcon
						message="Connection lost"
						description={pollError}
						style={{ marginTop: 16 }}
					/>
					{renderRaisedIhiReviews({ marginTop: 16 })}
				</>
			);
		}

		if (!mergeResult) return null;

		const { status, per_service_results } = mergeResult;

		const overallType =
			status === "completed"
				? "success"
				: status === "partial_failure"
				? "warning"
				: "error";

		const overallMessage =
			status === "completed"
				? "Merge completed successfully across all services."
				: status === "partial_failure"
				? "Merge completed with partial failures — some services could not be updated."
				: "Merge failed — no records were moved.";

		return (
			<div style={{ marginTop: 16 }}>
				<Alert
					type={overallType}
					showIcon
					message={overallMessage}
					style={{ marginBottom: 16 }}
				/>

				{renderRaisedIhiReviews()}

				{per_service_results && (
					<>
						<Text strong style={{ display: "block", marginBottom: 8 }}>
							Per-service results
						</Text>
						<List
							size="small"
							bordered
							dataSource={Object.entries(per_service_results)}
							renderItem={([service, result]) => {
								const mergedSummary =
									Object.keys(result.merged).length > 0
										? Object.entries(result.merged)
												.map(([table, count]) => `${table}: ${count}`)
												.join(", ")
										: "—";

								return (
									<List.Item
										extra={
											result.success ? (
												<Tag icon={<CheckCircleOutlined />} color="success">
													Success
												</Tag>
											) : (
												<Tag icon={<CloseCircleOutlined />} color="error">
													Failed
												</Tag>
											)
										}
									>
										<List.Item.Meta
											title={
												<Text style={{ textTransform: "capitalize" }}>
													{service}
												</Text>
											}
											description={
												result.success ? (
													<Text type="secondary" style={{ fontSize: 12 }}>
														{mergedSummary}
													</Text>
												) : (
													<Text type="danger" style={{ fontSize: 12 }}>
														{result.error || "Unknown error"}
													</Text>
												)
											}
										/>
									</List.Item>
								);
							}}
						/>
					</>
				)}

				{status !== "failed" && (
					<Alert
						data-testid="patients-merge-survivor-check"
						type="info"
						showIcon
						message="Check the surviving record"
						description="Open the surviving patient to confirm its current IHI, IHI number status and IHI record status. Any IHI warning or alert raised during the merge is listed in HI Reviews."
						style={{ marginTop: 16 }}
					/>
				)}
			</div>
		);
	};

	const renderOutcome = () =>
		outcome && (
			<Alert
				data-testid="patients-merge-outcome"
				type={outcome.severity}
				showIcon
				message={outcome.title}
				description={
					<>
						<div>{outcome.detail}</div>
						{outcome.ihiReviews && outcome.ihiReviews.length > 0 && renderIhiReviewList(outcome.ihiReviews)}
						{(outcome.seeHiReviews || (outcome.ihiReviews?.length ?? 0) > 0) && hiReviewsLink()}
					</>
				}
				style={{ marginTop: 16 }}
			/>
		);

	const renderAmend = () => (
		<div style={{ marginTop: 16 }}>
			<Alert
				type="info"
				showIcon
				message="Amend the Medicare IHI search"
				description="Enter the corrected details for one resubmission of the merged record's Medicare IHI search. They are used for this search only and are not saved to either patient record."
				style={{ marginBottom: 16 }}
			/>
			{amendError && (
				<Alert
					type="warning"
					showIcon
					message={amendError.title}
					description={amendError.detail}
					style={{ marginBottom: 16 }}
					data-testid="patients-merge-amend-error"
				/>
			)}
			<Form form={amendForm} layout="vertical" preserve={false} autoComplete="off" data-testid="patients-merge-amend-form">
				{MERGE_AMENDED_SEARCH_FIELDS.map(renderAmendField)}
			</Form>
		</div>
	);

	const renderTimeout = () => (
		<>
			<Alert
				type="warning"
				showIcon
				message="Still processing"
				description="The merge is taking longer than expected. It will continue in the background — refresh the patient list in a minute to see the updated records."
				style={{ marginTop: 16 }}
			/>
			{renderRaisedIhiReviews({ marginTop: 16 })}
		</>
	);

	// ── Footer ────────────────────────────────────────────────────────────────

	const footer =
		phase === "select"
			? [
					<Button key="cancel" onClick={cancelMerge}>
						Cancel
					</Button>,
					<Button
						key="confirm"
						type="primary"
						danger
						loading={isSubmitting}
						onClick={handleConfirm}
					>
						Confirm Merge {mergeDirection === "up" ? "↑" : "↓"}
					</Button>,
			  ]
			: phase === "polling"
			? [] // no footer while polling — user must wait
			: phase === "outcome"
			? [
					<Button key="close" onClick={cancelMerge}>
						Close
					</Button>,
					...(outcome?.amendedSearch
						? [
								<Button
									key="amend"
									type="primary"
									data-testid="patients-merge-amend-btn"
									onClick={() => {
										setAmendError(null);
										setPhase("amend");
									}}
								>
									Amend search
								</Button>,
						  ]
						: []),
			  ]
			: phase === "amend"
			? [
					<Button key="back" onClick={handleAmendCancel} disabled={isSubmitting}>
						Cancel amendment
					</Button>,
					<Button
						key="submit"
						type="primary"
						loading={isSubmitting}
						data-testid="patients-merge-amend-submit"
						onClick={handleAmendSubmit}
					>
						Submit amended search
					</Button>,
			  ]
			: [
					<Button key="close" type="primary" onClick={cancelMerge}>
						Close
					</Button>,
			  ];

	return (
		<Modal
			title={
				<Space>
					<MergeCellsOutlined />
					<span>Merge Patient Profiles</span>
				</Space>
			}
			open={modalOpen}
			onCancel={phase === "polling" ? undefined : cancelMerge}
			closable={phase !== "polling"}
			maskClosable={phase !== "polling"}
			width={700}
			footer={footer}
		>
			{phase === "select" && renderSelect()}
			{phase === "polling" && renderPolling()}
			{phase === "done" && renderResults()}
			{phase === "timeout" && renderTimeout()}
			{phase === "outcome" && renderOutcome()}
			{phase === "amend" && renderAmend()}
		</Modal>
	);
};

export default MergePatientModal;
